<template>
  <SeranovaModal
    data-cy="multi-pay-modal"
    size="large"
    body-padding="none"
    show-close-button
    :model-value="true"
    @update:model-value="(v: boolean) => !v && $emit('hide-modal')"
  >
    <template #header>
      <div
        class="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-700"
        data-cy="multi-pay-modal-title"
      >
        <strong class="font-semibold text-gray-900"
          >Pay {{ invoices.length }} invoices</strong
        >
        <span class="text-gray-400">·</span>
        <span>{{ paymentCustomer?.full_name }}</span>
        <span class="text-gray-400">·</span>
        <span
          class="font-mono text-xs tabular-nums text-gray-900 bg-gray-100 px-2 py-0.5 rounded"
          data-cy="multi-pay-modal-total-due"
        >
          {{ dollars(totalCents) }} due
        </span>
      </div>
    </template>

    <div
      class="flex flex-col min-w-0 divide-y divide-gray-200 [&>*]:px-5 [&>*]:py-3"
    >
      <section aria-label="Payment amount" data-cy="multi-pay-amount-section">
        <div class="flex items-center justify-between mb-2">
          <div class="block text-sm font-medium text-gray-900">Amount</div>
          <span
            class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700"
            data-cy="multi-pay-amount-lock"
          >
            <i class="fa-regular fa-lock" aria-hidden="true"></i>
            Total of selected invoices
          </span>
        </div>
        <div
          class="font-mono text-3xl tabular-nums text-gray-900"
          data-cy="multi-pay-amount"
        >
          {{ dollars(totalCents) }}
        </div>
        <p class="mt-1 text-xs text-gray-600">
          Each selected invoice will be paid in full. To pay a partial amount,
          deselect the others and use the single-invoice flow.
        </p>
      </section>

      <section aria-label="Invoices being paid" data-cy="multi-pay-invoices">
        <div class="block text-sm font-medium text-gray-900 mb-2">
          Invoices being paid
        </div>
        <div
          class="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100"
        >
          <div
            v-for="invoice in invoices"
            :key="invoice.invoice_id"
            class="flex items-center gap-3 px-3 py-2 text-sm"
            :data-cy="`multi-pay-invoice-row-${invoice.invoice_id}`"
          >
            <span class="font-medium text-blue-700">{{ invoice.label }}</span>
            <span class="text-gray-500">{{ invoice.invoice_date }}</span>
            <span class="ml-auto font-mono tabular-nums text-gray-900">
              {{ dollars(invoice.balance_cents) }}
            </span>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700"
            >
              <i class="fa-regular fa-check" aria-hidden="true"></i>
              Paid in full
            </span>
          </div>
          <div
            class="flex items-center justify-between px-3 py-2 text-sm bg-gray-50"
          >
            <span class="text-gray-500">Total</span>
            <span class="font-mono tabular-nums font-semibold text-gray-900">
              {{ dollars(totalCents) }}
            </span>
          </div>
        </div>
      </section>

      <section
        v-show="!confirmStep"
        aria-label="Payment method"
        data-cy="multi-pay-method-section"
      >
        <div class="block text-sm font-medium text-gray-900 mb-2">Method</div>

        <Spinner v-if="loadingMethods" message="Loading payment methods…" />

        <template v-else>
          <MethodTileGroup
            v-model="paymentType"
            :methods="methodTiles"
            data-cy="multi-pay-methods"
            aria-label="Payment method"
          />

          <div class="mt-3 min-w-0 -mx-5 px-5 pt-3 border-t border-gray-200">
            <PaymentMethods
              v-if="paymentType && paymentCustomer"
              ref="paymentForm"
              v-model="paymentType"
              :invoice-id="primaryInvoiceId"
              :customer="paymentCustomer"
              :payment-amount-cents="totalCents"
              :financing-enabled="false"
              :chromeless="true"
              data-cy="multi-pay-payment-methods"
            />
          </div>
        </template>
      </section>

      <section
        v-if="confirmStep"
        aria-label="Confirm payment"
        data-cy="multi-pay-confirm-panel"
        class="bg-amber-50"
      >
        <div class="text-sm font-medium text-gray-900 mb-1">
          Are you sure you want to pay these specific invoices?
        </div>
        <p class="text-sm text-gray-700">
          You're about to pay {{ invoices.length }} invoices in full ({{
            invoiceLabels
          }}) — {{ dollars(totalCents) }} via {{ confirmDestination }}. This
          payment can't be split or partially applied.
        </p>
      </section>

      <InlineNote
        v-if="errorMessage"
        tone="warn"
        data-cy="multi-pay-error"
        class="mx-5 my-3"
      >
        {{ errorMessage }}
      </InlineNote>
    </div>

    <template #footer>
      <StickyConfirmFooter
        v-if="!confirmStep"
        tone="pay"
        verb-override="Review payment"
        :amount-cents="totalCents"
        :destination="confirmDestination"
        :saving="saving"
        :disabled="!paymentType || loadingMethods"
        @submit="confirmStep = true"
        @cancel="$emit('hide-modal')"
      />
      <StickyConfirmFooter
        v-else
        tone="pay"
        :amount-cents="totalCents"
        :destination="confirmDestination"
        :saving="saving"
        :disabled="saving"
        data-cy="multi-pay-confirm-footer"
        @submit="handlePay"
        @cancel="confirmStep = false"
      />
    </template>
  </SeranovaModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import InlineNote from "@/components/PaymentModals/InlineNote.vue";
import MethodTileGroup from "@/components/PaymentModals/MethodTileGroup.vue";
import StickyConfirmFooter from "@/components/PaymentModals/StickyConfirmFooter.vue";
import SeranovaModal from "@/components/Seranova/Modal/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { dollars } from "@/mixins";
import { buildMethodTiles, confirmDestinationFor } from "./methodTiles";
import PaymentMethods from "./PaymentMethods.vue";
import usePaymentMethods from "./usePaymentMethods";

export interface SelectedInvoiceSummary {
  invoice_id: number;
  label: string;
  invoice_date: string;
  balance_cents: number;
  payable: boolean;
}

const props = defineProps<{
  customerId: number;
  invoices: SelectedInvoiceSummary[];
  receiversUrl: string;
}>();

const emit = defineEmits<{
  (e: "payment-success"): void;
  (e: "hide-modal"): void;
  (e: "stale"): void;
}>();

const store = useStore();
const {
  customer,
  paymentMethodsResponse,
  fetchPaymentMethods,
  fetchPaymentReceivers,
  fetchCustomer
} = usePaymentMethods();

const paymentForm = ref<{ getData: () => Promise<any> } | null>(null);
const paymentType = ref("");
const saving = ref(false);
const loadingMethods = ref(true);
const confirmStep = ref(false);
const errorMessage = ref("");

const totalCents = computed(() =>
  props.invoices.reduce((sum, invoice) => sum + invoice.balance_cents, 0)
);
const primaryInvoiceId = computed(() => props.invoices[0]?.invoice_id ?? null);
const invoiceLabels = computed(() =>
  props.invoices.map((invoice) => `#${invoice.invoice_id}`).join(", ")
);
// The composable's customer comes from the users endpoint (id), but
// PaymentMethods re-fetches by customer_id — normalize so its internal
// fetch never runs with undefined and wipes the shared ref.
const paymentCustomer = computed(() => {
  const value = customer.value as any;
  if (!value) return null;
  return { ...value, customer_id: value.customer_id ?? value.id };
});
const methodTiles = computed(() =>
  buildMethodTiles(paymentMethodsResponse.value, paymentCustomer.value)
);
const confirmDestination = computed(() =>
  confirmDestinationFor(paymentType.value, paymentCustomer.value)
);

onMounted(async () => {
  fetchPaymentReceivers(props.receiversUrl);
  // usePaymentMethods is a window-global singleton — always re-fetch on
  // mount so state left by the single-invoice modal can't leak in. The two
  // fetches are independent (methods come from the tenant endpoint).
  await Promise.all([fetchCustomer(props.customerId), fetchPaymentMethods()]);
  if (!paymentType.value) {
    const enabledFirst = methodTiles.value.find((tile) => !tile.disabled);
    if (enabledFirst) paymentType.value = String(enabledFirst.id);
  }
  loadingMethods.value = false;
});

async function handlePay() {
  if (saving.value) return;
  saving.value = true;
  errorMessage.value = "";
  try {
    const formData = await paymentForm.value?.getData();
    if (!formData) {
      saving.value = false;
      confirmStep.value = false;
      return;
    }
    const data = {
      invoice_ids: props.invoices.map((invoice) => invoice.invoice_id),
      expected_total_cents: totalCents.value,
      ...formData
    };
    await store.dispatch("createMultiInvoicePayment", {
      customerId: props.customerId,
      data
    });
    (window as any).notify?.success?.("Payment processed successfully!");
    emit("payment-success");
    emit("hide-modal");
    if (formData.card_save_payment_method || formData.ach_save_payment_method) {
      EventBus.$emit("payment-method-updated");
    }
  } catch (err: any) {
    confirmStep.value = false;
    errorMessage.value =
      err?.response?.data?.message ||
      "We couldn't process this payment. Review the invoices and try again.";
    if (err?.response?.status === 409) emit("stale");
  } finally {
    saving.value = false;
  }
}
</script>
